import { isPresent, Optional } from "#types";
import { ThunkAction, ThunkDispatch } from "redux-thunk";
import { AnyAction, Dispatch, Reducer, Store } from "redux";
import { EnvironmentState } from "#store/environment";
import { InterpretationState } from "#store/interpretations";
import { LoggingState } from "#store/logging";
import { TablesState } from "#store/tables";
import { TableContextState } from "#store/tablecontext";
import { OperatorInvocationState } from "#store/operatorInvocations";
import { UiState } from "#store/ui/uiTypes";
import { ActionsState } from "#store/actions/actionTypes";
import { DatasourceState } from "#store/datasources";
import { AutocompleteState } from "#store/autocomplete";
import { RouterState } from "redux-first-history";

export enum IoStateEnum {
    fetching = "fetching",
    done = "done",
    error = "error",
    initialized = "initialized",
}

export type InitializedState = {
    state: IoStateEnum.initialized;
    timestamp: Date;
    payload: null;
    error: null;
};

export type FetchingState = {
    state: IoStateEnum.fetching;
    timestamp: Date;
    payload: null;
    error: null;
};

export type DoneState<T> = {
    state: IoStateEnum.done;
    timestamp: Date;
    payload: T;
    error: null;
};

export type ErrorState = {
    state: IoStateEnum.error;
    timestamp: Date;
    error: Optional<ErrorPayload>;
    payload: null;
};

export type IoState<T = unknown> =
    | InitializedState
    | FetchingState
    | DoneState<T>
    | ErrorState;

export type ErrorPayload<T = Error> = {
    error: Optional<T>;
    id: PropertyKey;
    message?: Optional<string>;
};

export function isInitialized<T>(item: IoState): item is InitializedState {
    return item.state === IoStateEnum.initialized;
}

export function isFetching<T>(item: IoState): item is FetchingState {
    return item.state === IoStateEnum.fetching;
}

export function isDone<T>(item: Optional<IoState<T>>): item is DoneState<T> {
    return isPresent(item) && item.state === IoStateEnum.done;
}

export function isError(item: IoState): item is ErrorState {
    return item.state === IoStateEnum.error;
}

// The top-level store state definition
export interface ApplicationState {
    actions: ActionsState;
    environment: EnvironmentState;
    interpretations: InterpretationState;
    logging: LoggingState;
    operatorInvocations: OperatorInvocationState;
    router: RouterState;
    tables: TablesState;
    tableContext: TableContextState;
    uiState: UiState;
    datasources: DatasourceState;
    autocomplete: AutocompleteState;
}

export type StateType = ReturnType<Reducer<any, AnyAction>>;
// 👇 ensures that redux actions as well as redux thunks are accepted as dispatch parameter
export type AppDispatch = ThunkDispatch<StateType, undefined, AnyAction>;
// export type AppDispatch = Dispatch<AnyAction> & ThunkDispatch<StateType, undefined, AnyAction>;
export type AppStoreType = Store<ApplicationState> & { dispatch: AppDispatch };

/**
 * This block provides the types and hooks for redux.
 */
// Infer the `RootState` and `AppDispatch` types from the store itself
// export type ApplicationState = ReturnType<typeof AppStoreType.getState>;
export type GetState = () => ApplicationState;
export type ThunkResult<R = void> = ThunkAction<
    R,
    ApplicationState,
    null | undefined,
    AnyAction
>;
